// @flow

import React, { useContext, useEffect, useState } from 'react';
import { get, isFunction } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';

import { t, addTranslationKeys } from 'Src/i18n';
import { Button } from 'Components/frameworks/button/Button.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaModalWhen } from 'Components/widgets/criteria-modal/criteria-modal-when/CriteriaModalWhen.jsx';
import { CriteriaFacets } from 'Components/widgets/criteria/criteria-facets/CriteriaFacets.jsx';
import { CriteriaQuery } from 'Components/widgets/criteria/criteria-query/CriteriaQuery';
import { CriteriaTransport } from 'Components/widgets/criteria/criteria-transport/CriteriaTransport.jsx';
import { CriteriaWhen } from 'Components/widgets/criteria/criteria-when/CriteriaWhen.jsx';
import { CriteriaWho } from 'Components/widgets/criteria/criteria-who/CriteriaWho.jsx';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { useCriteriaDisplayContext } from 'Components/widgets/criteria/CriteriaDisplayContext';
import { useTrackQueryChange } from 'Components/widgets/facet-search/useTrackQueryChange';
import { facetsSelector, filteredProductsCountSelector } from 'Store/faceting/faceting.selector';
import { setIsSearchEnabled } from 'Store/faceting/facetingSearch.action';
import { DURATION_FACET } from 'Utils/faceting/constants';
import { setProductComparisonSelectionMode } from 'Store/faceting/productComparison.action';
import { getDepartureCitiesFromFacets } from './SearchForm.utils';

const SubmitButton = (props: { className?: string }) => {
  return (
    <div
      className={classnames(
        props.className,
        'rtl:ml-2 flex-none lg:justify-center lg:leading-none lg:mx-2',
      )}
    >
      <Button
        className="lg:rtl:ml-2 flex-none lg:mx-2 w-full"
        color={COLORS.WHITE}
        colorBg={COLORS.BLUE}
        colorBorder={COLORS.BLUE}
        minWidth="initial"
        size="large"
        type="submit"
      >
        <Icon name={ICONS.SEARCH_GLASS} />
      </Button>
    </div>
  );
};

export type Props = {
  i18n?: CMSContent,
  layerButtonLabel?: string,
  onSubmit?: (event?: Event) => void,
  showNbResultsInLayer?: boolean,
  withFacets?: boolean,
  withQuery?: boolean,
  doTrackQueryChange: boolean,
};

export const SearchForm = (props: Props) => {
  const [, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext] = useCriteriaDisplayContext();
  const [criteriaModalWhenIsOpen, setCriteriaModalWhenIsOpen] = useState(false);
  const trackQueryChange = useTrackQueryChange();
  const dispatch = useDispatch();

  const departureDate: ?moment$Moment = useSelector(state =>
    get(state, 'tripCriteria.departureDate'),
  );
  const duration: number = useSelector(state => get(state, 'tripCriteria.duration', 0));
  const facets: Array<*> = useSelector(state => facetsSelector(state));
  const resultsCount: number = useSelector(state => filteredProductsCountSelector(state));
  const isLoading: boolean = useSelector(state => state.faceting.isLoading);
  const isProductComparisonSelectionModeEnabled: boolean = useSelector(state =>
    get(state, 'faceting.isSelectionModeEnabled'),
  );

  const getOptionsDuration = () => {
    const results = (facets.find(f => f.id === DURATION_FACET) || { list: [] }).list;

    return results.map(opt => parseInt(opt.value, 10)).sort((a, b) => (a < b ? -1 : 1));
  };

  useEffect(
    () => {
      setCriteriaContext({
        layerButtonLabel: props.layerButtonLabel,
        layerHeader: props.showNbResultsInLayer
          ? t('faceting.viewResorts', {
              n: `<span class="font-bold text-blue">${resultsCount || 0}</span>`,
            })
          : '',
      });
    },
    [props.showNbResultsInLayer, resultsCount, props.layerButtonLabel],
  );

  useEffect(
    () => {
      if (
        criteriaDisplayContext.criteriaWhenIsOpen ||
        criteriaDisplayContext.criteriaTransportIsOpen
      ) {
        dispatch(setIsSearchEnabled(true));
      }
    },
    [criteriaDisplayContext.criteriaTransportIsOpen, criteriaDisplayContext.criteriaWhenIsOpen],
  );

  const updateTranslationKeys = () => {
    addTranslationKeys({
      common: get(props, 'i18n.common'),
      booking: get(props, 'i18n.booking'),
      faceting: get(props, 'i18n.faceting'),
    });
  };

  updateTranslationKeys();

  useEffect(
    () => {
      updateTranslationKeys();
    },
    [props.i18n],
  );

  useEffect(
    () => {
      const dateIsMissing = !departureDate || !duration;
      if (
        isProductComparisonSelectionModeEnabled &&
        dateIsMissing &&
        !criteriaDisplayContext.criteriaWhenIsOpen &&
        !criteriaModalWhenIsOpen
      ) {
        setCriteriaModalWhenIsOpen(true);
      }
    },
    [
      criteriaDisplayContext.criteriaWhenIsOpen,
      isProductComparisonSelectionModeEnabled,
      departureDate,
    ],
  );

  const departureCitiesOptions = getDepartureCitiesFromFacets(
    facets,
    t('faceting.transport.selectPlaceholder'),
  );

  const onSubmit = (event?: Event) => {
    if (event) {
      event.preventDefault();
    }
    if (isFunction(props.onSubmit)) {
      props.onSubmit(event);
    }
  };

  const onClose = () => {
    setCriteriaModalWhenIsOpen(false);

    if (!departureDate && !duration) {
      dispatch(setProductComparisonSelectionMode(false));
    }
  };

  const durations = getOptionsDuration();
  return (
    <form className="relative z-3" onSubmit={onSubmit}>
      <SiteContainer>
        <div className="mx-auto p-2 lg:flex mb-4 bg-white shadow-lg border-1 border-gray-light rounded-small lg:py-3 lg:px-1">
          {props.withQuery && (
            <div className="flex mb-2 lg:flex-1 lg:mb-0 lg:mx-2 lg:block">
              <div className="flex-1" data-testid="search-form-criteria-query">
                <CriteriaQuery
                  onQueryChange={query => props.doTrackQueryChange && trackQueryChange(query)}
                />
              </div>
              <SubmitButton className="lg:hidden" />
            </div>
          )}
          <div className="flex -mx-1 lg:mx-0 lg:flex-2">
            <div className="flex-1 lg:mx-2 px-1 lg:px-0" data-testid="search-form-criteria-when">
              <CriteriaWhen durationOptions={durations} />
            </div>
            <div className="flex-1 lg:mx-2 px-1 lg:px-0" data-testid="search-form-criteria-who">
              <CriteriaWho />
            </div>
            <div
              className="flex-1 lg:mx-2 px-1 lg:px-0"
              data-testid="search-form-criteria-transport"
            >
              <CriteriaTransport
                departureCitiesOptions={departureCitiesOptions}
                isLoading={isLoading}
              />
            </div>
          </div>
          <SubmitButton className="hidden lg:flex" />
        </div>
      </SiteContainer>
      {props.withFacets && <CriteriaFacets i18n={props.i18n} />}

      {criteriaModalWhenIsOpen && (
        <CriteriaModalWhen durationOptions={durations} onClose={onClose} />
      )}
    </form>
  );
};
